import { normalizeSyncBaseUrl, type SyncClientSettings } from "./syncSettings";

/**
 * Board snapshot send/pull (/v1/state, /v1/revision, /v1/live, /v1/schedule)
 * requires the sync server base URL and the Nanode sync token (Bearer matching
 * the server SYNC_TOKEN).
 *
 * These integration keys are not read by that path. A desk with none of them
 * still syncs once the URL and Nanode sync token are set.
 */
export const SCHEDULE_SYNC_KEYS_NOT_REQUIRED = [
  "xAI API key",
  "SimBrief API key",
  "Tracker API key",
  "VA ops key",
  "Mapbox token",
  "CheckWX API key",
  "FAA NOTAM client id",
  "FAA NOTAM client secret",
] as const;

export type ScheduleSyncFieldId = "sync-token" | "sync-url";

export type ScheduleSyncBlock = {
  ok: false;
  missing: "token" | "url" | "both";
  /** Settings section anchor. Token lives under Integrations / Secrets. */
  focusSection: "secrets" | "sync";
  fieldId: ScheduleSyncFieldId;
  chip: "SYNC blocked";
  tone: "alert";
  actionLabel: string;
  /** One line for the status chip. */
  label: string;
  /** Plain sentence for the banner and Settings. No secrets. */
  notice: string;
};

export type ScheduleSyncDiagnosis = { ok: true } | ScheduleSyncBlock;

const NOT_REQUIRED =
  "xAI, SimBrief, tracker, Mapbox, CheckWX, and FAA keys are not used for schedule sync.";

export function diagnoseScheduleSync(
  settings: Pick<SyncClientSettings, "baseUrl" | "token">,
): ScheduleSyncDiagnosis {
  const url = normalizeSyncBaseUrl(settings.baseUrl ?? "");
  const token = String(settings.token ?? "").trim();
  if (url && token) return { ok: true };
  const missing = !url && !token ? "both" : !token ? "token" : "url";
  if (missing === "url") {
    return {
      ok: false,
      missing,
      focusSection: "sync",
      fieldId: "sync-url",
      chip: "SYNC blocked",
      tone: "alert",
      actionLabel: "Open sync server URL",
      label: "Sync server URL is not set",
      notice: `Schedule sync is blocked. This desk has no sync server URL, so trips and the theme stay on this PC and the phone never receives them. ${NOT_REQUIRED} Set the URL under Settings → System → Data / Sync.`,
    };
  }
  return {
    ok: false,
    missing,
    focusSection: "secrets",
    fieldId: "sync-token",
    chip: "SYNC blocked",
    tone: "alert",
    actionLabel: "Open Nanode sync token",
    label: missing === "both" ? "Nanode sync token and server URL are not set" : "Nanode sync token is not set",
    notice:
      missing === "both"
        ? `Schedule sync is blocked. This desk has no Nanode sync token and no sync server URL, so trips and the theme stay on this PC. The phone reads the sync server, which never receives them. ${NOT_REQUIRED} Paste the Nanode sync token (the server SYNC_TOKEN) under Settings → System → Integrations / Secrets. Set the sync server base URL under Data / Sync.`
        : `Schedule sync is blocked. This desk has no Nanode sync token, so trips and the theme stay on this PC. The phone reads the sync server, which never receives them. ${NOT_REQUIRED} Paste the Nanode sync token (the server SYNC_TOKEN) under Settings → System → Integrations / Secrets.`,
  };
}

/** HTTP 200 with an empty body is a failed pull, not an empty schedule. */
export function parseSyncJsonBody(
  status: number,
  text: string,
): { ok: true; value: unknown } | { ok: false; error: string; status: number } {
  const body = String(text ?? "");
  if (!body.trim()) {
    if (status >= 200 && status < 300) {
      return { ok: false, error: "Sync server returned HTTP 200 with an empty body.", status };
    }
    return { ok: false, error: `Sync server returned HTTP ${status} with an empty body.`, status };
  }
  try {
    return { ok: true, value: JSON.parse(body) as unknown };
  } catch {
    return { ok: false, error: "Sync server returned a response that is not JSON.", status };
  }
}
